import React, {useEffect, useState} from 'react';

import {skipToken} from '@reduxjs/toolkit/query';
import find from 'lodash/find';
import {withRouter, WithRouterProps} from 'react-router';
import styled from 'styled-components';

import {useCurrentUserState} from 'app/current-user/api';
import {DocSidebarPagePanels} from 'app/general/structures/DocSidebarPagePanels';
import StateButton from 'app/general/widgets/StateButton';
import {useLanesForDocument} from 'app/lanes/api';
import {deleteProcedureVersion} from 'app/procedures/actions';
import {useAccountSettingsState} from 'app/settings/api';
import {useModelChangeSubscription} from 'app/subscriptions';
import * as urls from 'app/urls';
import {reportUnexpectedState} from 'app/utils/errors';
import {gt} from 'app/utils/i18n';
import {whenVisible} from 'app/utils/page-visibility';
import {useAsyncActions} from 'app/utils/redux';
import {getPathFromURL} from 'app/utils/urls';
import {groupVersions} from 'app/version/utils';

import {
  GroupedVersionsItem,
  removeSelectedVersionGroupParam,
} from './GroupedVersionsItem';
import {LocationQuery} from './location-query';
import {VersionItem} from './VersionItem';

import type {AbstractVersionType} from 'app/version/types';

export const StyledListDIV = styled.div`
  .list-group {
    border-radius: 0;
  }

  .list-group-item {
    border-radius: 0;
    border-left: none;
    border-right: none;

    &.active {
      z-index: unset;
    }
  }
`;

type RouterProps = WithRouterProps<Record<string, never>, LocationQuery>;

interface Props<T extends AbstractVersionType> extends RouterProps {
  canDelete: boolean;
  collapsed: boolean;
  onToggleCollapsed: () => void;
  selectedVersion?: T;
  closeSidebar: () => void;
  loadVersions: (objectId: number | string) => Promise<any>;
  loadMoreVersions: (objectId: number | string) => Promise<any>;
  objectId: number | string;
  moreToLoad: boolean;
  versions: Array<T>;
  contentType: 'procedure' | 'policy' | 'process' | 'quiz';
}

const VersionsSidebar = <T extends AbstractVersionType>(props: Props<T>) => {
  let {
    moreToLoad,
    canDelete,
    versions,
    closeSidebar,
    location,
    collapsed,
    onToggleCollapsed,
    selectedVersion,
    contentType,
  } = props;
  const {query} = location;
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const {is_peruser, version_days, owner_email} =
    useAccountSettingsState().data!;
  const {is_owner} = useCurrentUserState().data!;

  const actions = useAsyncActions({deleteVersion: deleteProcedureVersion});

  const versionsIds = versions.map(({id}) => id).join(',');
  const lanesCt = lanesContentTypeMap[contentType];
  useLanesForDocument(
    lanesCt == null || versions.length == 0
      ? skipToken
      : {
          content_type: lanesCt,
          object_ids: versionsIds,
        }
  );

  useModelChangeSubscription(
    (message, {runWhenAllRelatedMatch}) => {
      const {objectId, contentType} = props;
      runWhenAllRelatedMatch(
        message,
        `${contentType}version`,
        {
          content_type: contentType,
          id: objectId,
        },
        () => fetchData()
      );
    },
    [props.objectId, props.contentType]
  );

  const fetchData = whenVisible(() =>
    props.loadVersions(props.objectId).then(
      () => setIsLoading(false),
      () => setIsLoading(false)
    )
  );

  const isSelected = (versionId: number | string) => {
    const selectedVersion = props.selectedVersion;
    return Boolean(
      (selectedVersion && versionId === (selectedVersion.id || 0)) ||
        (!selectedVersion && !versionId)
    );
  };

  useEffect(() => {
    fetchData();
  }, []);

  const upgradeActionWidget = () => (
    <p>
      {!is_owner ? (
        <a href={`mailto:${owner_email}`}>
          {gt('Contact your account owner to upgrade')}
        </a>
      ) : (
        <a href={urls.UPGRADE}>{gt('Upgrade now')}</a>
      )}
    </p>
  );

  const deleteVersion = (version: T) => {
    const {router, versions, location} = props;
    return actions.deleteVersion(version.url).then(() => {
      if (isSelected(version.id)) {
        const versionIndex = versions.map((v) => v.id).indexOf(version.id);
        const previousVersion =
          versions[versionIndex - 1] ||
          find(versions, (v) => v.id !== version.id);

        if (previousVersion != null) {
          router.replace({
            pathname: getPathFromURL(previousVersion.html_url),
            query: location.query,
          });
        } else {
          reportUnexpectedState('USEC5');
        }
      }
    });
  };

  const showMessage = !is_peruser && version_days > 0;

  return collapsed === true ? null : (
    <DocSidebarPagePanels
      onClose={closeSidebar}
      loading={isLoading}
      title={
        <span>
          {gt('Version History')}
          <button className="close d-none d-sm-inline" onClick={closeSidebar}>
            <i className="fa fa-times" />
          </button>
          <button className="close d-sm-none" onClick={onToggleCollapsed}>
            <i className="fa fa-chevron-left" />
          </button>
        </span>
      }
      showCloseButton={false}
    >
      <StyledListDIV>
        {showMessage === true ? (
          <div className="m-3 small text-secondary font-italic">
            <p>
              {gt(
                'We provide %s days of version history for all your procedures and policies. If you would like up to 1 year history, request a change in your subscription to per user pricing',
                version_days
              )}
            </p>
            {upgradeActionWidget()}
          </div>
        ) : null}

        <ul className="list-group">
          {groupVersions(versions).map((group) => (
            <React.Fragment key={group[0].id}>
              {group.length === 1 ? (
                <VersionItem
                  canDelete={canDelete}
                  version={group[0]}
                  isSelected={isSelected(group[0].id)}
                  searchParams={removeSelectedVersionGroupParam(query)}
                  onDelete={() => deleteVersion(group[0])}
                />
              ) : (
                <GroupedVersionsItem
                  canDelete={canDelete}
                  versionGroup={group}
                  router={props.router}
                  location={location}
                  selectedVersion={selectedVersion ? selectedVersion.id : null}
                  deleteVersion={deleteVersion}
                />
              )}
            </React.Fragment>
          ))}
        </ul>

        {moreToLoad && (
          <div className="text-center my-2 mx-4 pb-2">
            <StateButton
              className="btn btn-secondary btn-sm btn-block"
              busyText={gt('Loading...')}
              onClick={() => props.loadMoreVersions(props.objectId)}
            >
              {gt('Load more')}
            </StateButton>
          </div>
        )}
      </StyledListDIV>
    </DocSidebarPagePanels>
  );
};

export function VersionsSidebarFactory<
  T extends AbstractVersionType
>(): React.ComponentType<Omit<Props<T>, keyof RouterProps>> {
  return withRouter<Props<T>>(VersionsSidebar);
}

/* types to be used by VersionsSidebar wrappers */
export type SpecificVersionsSidebarOwnProps = {
  canDelete: boolean;
  selectedVersion: any;
  closeSidebar: () => void;
  objectId: number | string;
  collapsed: boolean;
  onToggleCollapsed: () => void;
};

export type SpecificVersionsSidebarStateProps<T extends AbstractVersionType> = {
  moreToLoad: boolean;
  versions: Array<T>;
  contentType: 'procedure' | 'policy' | 'process';
};

const lanesContentTypeMap = {
  procedure: 'procedureversion',
  process: 'processversion',
} as const;
